/**
 * Wie ein Objekt im Speicher heißt — und woran der Server erkennt, dass ein Name von
 * ihm selbst stammt (ARCHITEKTUR.md 6.4, D5).
 *
 * ⚠️ Diese Datei importiert bewusst NICHTS. Sie wird vom Browser **und** vom Server
 * gelesen, und im Browser gibt es keine Node-Typen. Ein Import, der über zwei Ecken
 * `node:sqlite` hereinzieht, bricht den Typcheck des Clients — in `momentchen` ist
 * genau das gemessen worden und steht dort im Kopf von `lib/keys.ts`. Wer hier einen
 * Import hinzufügt, lässt `npm run typecheck` mitlaufen.
 */

/**
 * Die Zugriffsregel, die **jeder** presignte PUT mitsigniert.
 *
 * Gemessen am 21.09.2026 gegen `momentchen-test`, übernommen für Annas Bucket
 * (ARCHITEKTUR.md 4.12): Hetzners Ceph-RGW ist für CVE-2026-54330 anfällig — es prüft
 * nur, was in `X-Amz-SignedHeaders` steht, und führt jede andere `x-amz-*`-Kopfzeile
 * trotzdem aus. Wer eine presignte PUT-URL hat, könnte sein Bild damit
 * `public-read` setzen und dauerhaft öffentlich machen. Das bricht genau die Zusage,
 * die den privaten Bucket trägt (6.4).
 *
 * Signieren wir die Kopfzeile selbst, **muss** sie in jeder Anfrage stehen und genau
 * diesen Wert tragen; ein anderer bricht die Signatur. Ein `x-amz-grant-*` daneben
 * macht die Anfrage ungültig, weil S3 Canned-ACL und Header-Grants gegenseitig
 * ausschließt. Beide ACL-Wege sind damit zu, ohne dass Hetzner etwas tun muss.
 *
 * Der Wert steht hier und nirgends sonst: der Server signiert ihn (`server/storage.ts`),
 * der Browser sendet ihn (`src/lib/api.ts`). Zwei Schreibweisen, die auseinanderlaufen,
 * sind 403 auf **jeden** Upload — der teure Fehler, nicht der Angriff.
 */
export const OBJECT_ACL = "private";

/** Die Kopfzeile dazu, kleingeschrieben wie in `X-Amz-SignedHeaders`. */
export const OBJECT_ACL_HEADER = "x-amz-acl";

/**
 * Was hochgeladen werden darf. Nur Bilder — dieses Produkt kennt keine Videos, und
 * damit entfällt gegenüber `momentchen` der gesamte Multipart-Weg (siehe
 * `MAX_ORIGINAL_BYTES`).
 *
 * ⚠️ **HEIC/HEIF sind dabei, und das hat einen Preis:** aus ihnen lassen sich die
 * Ortsdaten nicht entfernen (`shared/gps.ts`), das Original geht also mit dem
 * Aufnahmeort zu Anna. iPhones liefern HEIC, wenn die Kundschaft in den Einstellungen
 * nicht „Maximale Kompatibilität" stehen hat. Sie abzuweisen verlöre Fotos, sie
 * anzunehmen kostet Datensparsamkeit — **das ist eine Entscheidung für Carolin und
 * steht in `TODO.md`**, nicht hier stillschweigend entschieden.
 */
export const ALLOWED_CONTENT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

const CONTENT_TYPE_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
};

const EXT_CONTENT_TYPE: Record<string, string> = {
  jpg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};

/**
 * Obergrenze je Original.
 *
 * ⚠️ **Übernommen aus `momentchen`, nicht entschieden.** AB § 1.1 führt „Dateigrößen"
 * als Klärungspunkt, und ein Ergebnis fehlt (ARCHITEKTUR.md 6.4, Abschnitt 9). 50 MB
 * sind großzügig genug, dass kein Handyfoto daran scheitert; die Zahl gehört Anna
 * vorgelegt, bevor sie in die Festpreiskalkulation eingeht.
 *
 * Weil sie unter Hetzners Einzel-PUT-Grenze von 5 GB liegt, braucht dieses Produkt
 * **keinen Multipart-Upload** — der halbe Aufwand von `momentchen`s Speicherschicht
 * (drei Endpunkte, ein Aufräum-Lauf für angefangene Uploads) entfällt damit.
 */
export const MAX_ORIGINAL_BYTES = 52_428_800;

/**
 * Obergrenze je Vorschaubild. Eine ~400-px-JPEG liegt realistisch unter 100 KB; der
 * Deckel ist so hoch, weil er nicht die Regel prüft, sondern den Unfall abfängt — die
 * PUT-URL für die Vorschau bindet keine `Content-Length`, und ohne Deckel ließe sich
 * darüber der Speicher vollschreiben.
 */
export const MAX_PREVIEW_BYTES = 2_097_152;

/**
 * Unterhalb dieser kurzen Bildkante erscheint der Auflösungshinweis (ARCHITEKTUR.md
 * 6.4). Reiner Pixelvergleich, **keine Bilderkennung** — das wäre V3 (Lastenheft 10.3).
 *
 * *Warum die kurze Kante und nicht die Fläche:* Wer bis zu den Knien sichtbar ist,
 * füllt die kurze Seite des Bildes fast aus — im Hochformat die Breite, im Querformat
 * die Höhe. Die kurze Kante ist damit der beste Stellvertreter für „wie viele Pixel
 * hoch wird die Person", solange der Umriss aus Schritt 3 noch nicht existiert.
 *
 * ⚠️ **400 ist gesetzt, nicht gemessen.** Geprüft ist nur, dass die Linie die richtigen
 * Bilder trennt: alle fünf absichtlich kleinen Fassungen in `fixtures/personen/`
 * liegen darunter (kürzeste Kante 120–160 px), alle 36 regulären darüber (ab 420 px).
 * Ob sie zu Annas Malpraxis passt, weiß nur sie — steht in `TODO.md`.
 */
export const MIN_SHORT_EDGE = 400;

/** Die Endung, unter der ein Bild dieses Typs abgelegt wird. */
export function extensionFor(contentType: string): string | null {
  if (!ALLOWED_CONTENT_TYPES.has(contentType)) return null;
  // Der Schlüssel kommt aus der geprüften Menge darüber, nie aus einer Eingabe.
  return CONTENT_TYPE_EXT[contentType] ?? null;
}

/**
 * Der Präfix, unter dem alles liegt, was zu **einem** Projekt gehört:
 *
 * ```
 * p/<projektId>/original/<bildId>.<ext>
 * p/<projektId>/vorschau/<bildId>.jpg
 * ```
 *
 * Die Trennung hält im **Speicher** und nicht nur in der Datenbank. Was das ermöglicht
 * und weshalb es kein Aufräumen ist: eine abgelaufene Collage lässt sich als ein
 * Präfix löschen statt als Liste einzelner Schlüssel, und der Server kann fragen, ob
 * ein Schlüssel aus einem Anfragerumpf überhaupt zu **dieser** Sitzung gehört. Ohne
 * die ID im Schlüssel stünde zwischen den Bildern zweier Collagen nur die unratbare
 * Bild-ID — kein Loch, aber auch keine Grenze.
 */
export const PROJECT_PREFIX = "p/";

/**
 * Die Form einer Projekt-ID: 16 Zufallsbytes als base64url, also 22 Zeichen aus
 * `[A-Za-z0-9_-]` (`server/crypto.ts`, `createProjectId`).
 *
 * Das Alphabet ist der Grund, weshalb die Prüfungen weiter unten am **rohen** String
 * arbeiten dürfen — siehe `keyBelongsTo`.
 */
const PROJECT_ID_INNER = "[A-Za-z0-9_-]{22}";
const PROJECT_ID_RE = new RegExp(`^${PROJECT_ID_INNER}$`);

/** Bild-IDs sind UUIDv4 und werden ausschließlich auf dem Server gezogen. */
const IMAGE_ID_INNER = "[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
const IMAGE_ID_RE = new RegExp(`^${IMAGE_ID_INNER}$`);

const EXT_GROUP = "(?:jpg|png|webp|heic|heif)";

// Die Projekt-ID steht in einer **Fanggruppe**, damit `projectIdFromKey` sie aus
// demselben Muster zieht, an dem `isOriginalKey` die Form prüft. Zwei Muster für
// dieselbe Frage wären zwei Wahrheiten, und die eine dürfte irgendwann strenger sein
// als die andere.
const ORIGINAL_KEY_RE = new RegExp(
  `^${PROJECT_PREFIX}(${PROJECT_ID_INNER})/original/${IMAGE_ID_INNER}\\.${EXT_GROUP}$`,
);
const PREVIEW_KEY_RE = new RegExp(
  `^${PROJECT_PREFIX}(${PROJECT_ID_INNER})/vorschau/${IMAGE_ID_INNER}\\.jpg$`,
);

export function isProjectId(value: unknown): value is string {
  return typeof value === "string" && PROJECT_ID_RE.test(value);
}

export function isImageId(value: unknown): value is string {
  return typeof value === "string" && IMAGE_ID_RE.test(value);
}

/** Wo die Originale **eines** Projekts liegen. */
export function originalPrefixFor(projectId: string): string {
  if (!isProjectId(projectId)) {
    throw new Error(`originalPrefixFor: keine Projekt-ID: ${projectId}`);
  }
  return `${PROJECT_PREFIX}${projectId}/original/`;
}

/** Wo die Vorschaubilder **eines** Projekts liegen. */
export function previewPrefixFor(projectId: string): string {
  if (!isProjectId(projectId)) {
    throw new Error(`previewPrefixFor: keine Projekt-ID: ${projectId}`);
  }
  return `${PROJECT_PREFIX}${projectId}/vorschau/`;
}

/**
 * Alles, was **einem** Projekt gehört. Ein Löschlauf arbeitet auf diesem Präfix; eine
 * ID, die keine ist, würde ihn aufweiten — im schlimmsten Fall bis `p/`, also auf jede
 * Collage. Deshalb wirft es.
 */
export function projectPrefixFor(projectId: string): string {
  if (!isProjectId(projectId)) {
    throw new Error(`projectPrefixFor: keine Projekt-ID: ${projectId}`);
  }
  return `${PROJECT_PREFIX}${projectId}/`;
}

export function originalKeyFor(projectId: string, imageId: string, contentType: string): string {
  const ext = extensionFor(contentType);
  if (ext === null) throw new Error(`originalKeyFor: unerlaubter Typ: ${contentType}`);
  if (!isImageId(imageId)) throw new Error(`originalKeyFor: keine Bild-ID: ${imageId}`);
  return `${originalPrefixFor(projectId)}${imageId}.${ext}`;
}

export function previewKeyFor(projectId: string, imageId: string): string {
  if (!isImageId(imageId)) throw new Error(`previewKeyFor: keine Bild-ID: ${imageId}`);
  return `${previewPrefixFor(projectId)}${imageId}.jpg`;
}

/**
 * Zu welchem Projekt gehört dieser Schlüssel — oder `null`, wenn er keine Form hat, die
 * dieses Produkt vergibt.
 *
 * **Das ist die Formprüfung, nicht eine daneben.** Ein Aufräumlauf darf nur löschen, was
 * er versteht; deshalb beantwortet dieselbe Funktion beides, und ein `null` heißt
 * „Finger weg" statt „egal". Bei `momentchen` steht der Satz mit derselben Begründung
 * über `weddingIdFromKey`.
 */
export function projectIdFromKey(key: string): string | null {
  // `exec` statt zwei Prüfungen: das Muster kennt die Antwort schon.
  const treffer = ORIGINAL_KEY_RE.exec(key) ?? PREVIEW_KEY_RE.exec(key);
  return treffer?.[1] ?? null;
}

export function isOriginalKey(value: unknown): value is string {
  return typeof value === "string" && ORIGINAL_KEY_RE.test(value);
}

export function isPreviewKey(value: unknown): value is string {
  return typeof value === "string" && PREVIEW_KEY_RE.test(value);
}

/**
 * Gehört dieser Schlüssel zu **diesem** Projekt?
 *
 * Gefragt wird gegen die ID aus der signierten Sitzung, nie gegen etwas aus dem Rumpf
 * — dieselbe Regel wie bei jeder Abfrage auf Mandantendaten (ARCHITEKTUR.md 6.3).
 *
 * Geprüft wird die **Form zuerst** und das Präfix danach. Andersherum wäre
 * `p/<fremde-id>/original/../../p/<eigene>/…` eine Zeichenkette, die mit dem eigenen
 * Präfix beginnt; erst die Form schließt aus, dass überhaupt etwas anderes darin
 * vorkommt. Ein Präfixvergleich allein ist keine Grenze, er sieht nur aus wie eine.
 *
 * ── Warum der rohe String genügt, obwohl über den normalisierten signiert wird
 *
 * `server/storage.ts` bildet die Signatur über den Pfad, den `encodeKey` und
 * `new URL()` hinterlassen; hier wird gegen die Zeichenkette geprüft, wie sie
 * hereinkam. Zwei verschiedene Dinge — sie fallen hier strukturell zusammen, nicht
 * zufällig:
 *
 *   1. Beide Muster sind mit `^` und `$` verankert und lassen nur
 *      `[A-Za-z0-9_-]`, Hex, die drei festen Segmente und die Endung durch. Ein
 *      angenommener Schlüssel enthält damit **kein** Zeichen, an dem eine
 *      Pfadnormalisierung arbeiten könnte: kein `.` außer dem vor der Endung, kein
 *      `%`, kein `\`, kein `//`, kein Leerzeichen, kein NUL.
 *   2. `encodeKey` ist auf diesem Alphabet die Identität, und `new URL()` hat auf
 *      einem Pfad ohne `.`, `..` oder `//` nichts zu tun. Roher String und signierter
 *      Pfad sind dieselbe Zeichenkette — nicht „meistens gleich".
 *
 * ⚠️ **Diese Begründung hängt vollständig an der Enge der beiden Muster.** Wird die
 * Form je erweitert — ein Dateiname der Kundschaft, ein Slug statt der ID, eine Endung
 * mit Sonderzeichen —, fällt Schritt 1, und dann muss gegen den **normalisierten**
 * Pfad geprüft werden. Wer das Muster anfasst, fasst diesen Absatz mit an.
 */
export function keyBelongsTo(key: unknown, projectId: string): key is string {
  if (!isOriginalKey(key) && !isPreviewKey(key)) return false;
  return (
    key.startsWith(originalPrefixFor(projectId)) || key.startsWith(previewPrefixFor(projectId))
  );
}

function extensionOf(key: string): string {
  return key.slice(key.lastIndexOf(".") + 1).toLowerCase();
}

/**
 * Der Inhaltstyp, den die Auslieferung erzwingt — abgeleitet aus dem Schlüssel, den
 * der Server selbst vergeben hat, nie aus den gespeicherten Bytes. Damit wird ein als
 * Bild hochgeladenes HTML-Dokument beim direkten Öffnen nicht als Seite ausgeführt.
 */
export function contentTypeFromKey(key: string): string {
  // Die Endung kommt aus einem Schlüssel, den `isOriginalKey`/`isPreviewKey` vorher
  // auf ein festes Alphabet eingeengt haben.
  return EXT_CONTENT_TYPE[extensionOf(key)] ?? "application/octet-stream";
}

/** Löst dieses Bild den Auflösungshinweis aus? Siehe `MIN_SHORT_EDGE`. */
export function tooSmallForPainting(width: number, height: number): boolean {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return false;
  if (width <= 0 || height <= 0) return false;
  return Math.min(width, height) < MIN_SHORT_EDGE;
}
